import { type TaskAuditLog, type Task } from 'src/api/fm/tasks/tasks.model'
import { type SortParamType } from 'src/components/parts/active-tasks/ActiveTasksTable'
import { sortByString, sortByNumber, sortByTimeStamp } from 'src/utils/sort/sort'
import { snakeCaseToDisplayName } from 'src/utils/string/stringUtils'

export interface Operator {
    id: string
    email: string
    isHunter: boolean
}

export function getTaskCreator(auditLog: TaskAuditLog[]): Operator | undefined {
    const event = findEvent('created', auditLog)

    return event ? getOperator(event) : undefined
}

export function getTaskCollector(auditLog: TaskAuditLog[]): Operator | undefined {
    const collectEvent = findEvent('collected', auditLog)
    if (collectEvent) {
        return getOperator(collectEvent)
    }

    const swapStartEvent = findEvent('battery_swap_started', auditLog)
    if (swapStartEvent) {
        return getOperator(swapStartEvent)
    }

    return undefined
}

export function getTaskDropper(auditLog: TaskAuditLog[]): Operator | undefined {
    const dropEvent = findEvent('dropped', auditLog)
    if (dropEvent) {
        return getOperator(dropEvent)
    }

    const swapFinalizedEvent = findEvent('battery_swap_finalized', auditLog)
    if (swapFinalizedEvent) {
        return getOperator(swapFinalizedEvent)
    }

    return undefined
}

// Currently the audit-log events `fulfilled` and `declined` will only be set
// when a FM-user manually fulfills or declines a task.
export function getTaskForceCloser(auditLog: TaskAuditLog[]): Operator | undefined {
    const fulfillEvent = findEvent('fulfilled', auditLog)
    if (fulfillEvent) {
        return getOperator(fulfillEvent)
    }

    const declineEvent = findEvent('declined', auditLog)
    if (declineEvent) {
        return getOperator(declineEvent)
    }

    return undefined
}

export function findEvent(type: string, auditLog: TaskAuditLog[]): TaskAuditLog | null {
    const event = auditLog.find(({ eventType }) => eventType === type)

    return event || null
}

export function getOperator(auditLogEvent: TaskAuditLog): Operator | undefined {
    const { operatorId, operatorEmail, operatorIsHunter } = auditLogEvent
    if (!operatorId || !operatorEmail) {
        return undefined
    }

    return {
        id: auditLogEvent.operatorId,
        email: auditLogEvent.operatorEmail,
        isHunter: operatorIsHunter ?? false,
    }
}

export function prettifyTaskType(taskType: string): string {
    return snakeCaseToDisplayName(taskType)
}

export const sortActiveTasks = (tasks: Task[], sortParam: SortParamType): Task[] => {
    if (!tasks || !tasks.length) {
        return []
    }
    if (sortParam.field === 'collected') {
        return sortByTimeStamp(tasks, 'collectState.time', sortParam.order)
    } else if (sortParam.field === 'createdAt') {
        return sortByTimeStamp(tasks, sortParam.field, sortParam.order)
    } else if (sortParam.field === 'executionPriority') {
        return sortByNumber(tasks, 'executionPriority.order', sortParam.order)
    } else {
        return sortByString(tasks, sortParam.field, sortParam.order)
    }
}
